import { Modal, Stack, Button, Group, TextInput, Accordion, Badge, ScrollArea, ActionIcon, Tooltip } from '@mantine/core';
import { IconSearch, IconStar, IconStarFilled, IconX } from '@tabler/icons-react';
import { useState } from 'react';

export interface FilterPreset<T> {
  id: string;
  name: string;
  description: string;
  icon?: React.ReactNode;
  color?: string;
  filters: T;
  badge?: string;
}

export interface SavedFilter<T> {
  id: string;
  name: string;
  description?: string;
  filters: T;
  isFavorite?: boolean;
  createdAt?: string;
}

interface FilterModalProps<T> {
  opened: boolean;
  onClose: () => void;
  title: string;
  filters: T;
  onFiltersChange: (filters: T) => void;
  onApply: () => void;
  onClear: () => void;
  onSave?: (name: string, description?: string) => void;
  onLoadSaved?: (filters: T) => void;
  onToggleFavorite?: (filterId: string) => void;
  onDeleteSaved?: (filterId: string) => void;
  children: React.ReactNode;
  presets?: FilterPreset<T>[];
  savedFilters?: SavedFilter<T>[];
  activeFilterCount?: number;
}

export function FilterModal<T>({
  opened,
  onClose,
  title,
  filters,
  onFiltersChange,
  onApply,
  onClear,
  onSave,
  onLoadSaved,
  onToggleFavorite,
  onDeleteSaved,
  children,
  presets = [],
  savedFilters = [],
  activeFilterCount = 0,
}: FilterModalProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState<string[]>(['filters']);

  const handleApply = () => {
    onApply();
    onClose();
  };

  const handleClear = () => {
    onClear();
    setSearchTerm('');
  };

  const handleSelectPreset = (preset: FilterPreset<T>) => {
    onFiltersChange(preset.filters);
    setActiveSection(['filters']);
  };

  const handleLoadSaved = (saved: SavedFilter<T>) => {
    if (onLoadSaved) {
      onLoadSaved(saved.filters);
      setActiveSection(['filters']);
    }
  };

  // Filter presets and saved filters based on search
  const filteredPresets = presets.filter(
    (preset) =>
      preset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      preset.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSavedFilters = savedFilters.filter((saved) =>
    saved.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const favoriteFilters = filteredSavedFilters.filter((f) => f.isFavorite);
  const regularFilters = filteredSavedFilters.filter((f) => !f.isFavorite);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={title}
      size="xl"
      padding="md"
      styles={{
        body: { paddingTop: 0 },
      }}
    >
      <Stack gap="md">
        {/* Search bar */}
        <TextInput
          placeholder="Search filters and presets..."
          leftSection={<IconSearch size={16} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          rightSection={
            searchTerm && (
              <ActionIcon variant="subtle" onClick={() => setSearchTerm('')} size="sm">
                <IconX size={14} />
              </ActionIcon>
            )
          }
        />

        {/* Scrollable content */}
        <ScrollArea h={500} offsetScrollbars>
          <Accordion
            multiple
            value={activeSection}
            onChange={setActiveSection}
            variant="separated"
          >
            {/* Presets Section */}
            {presets.length > 0 && (
              <Accordion.Item value="presets">
                <Accordion.Control>
                  <Group justify="apart">
                    <span>Quick Presets</span>
                    <Badge size="sm" variant="light">
                      {filteredPresets.length}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    {filteredPresets.map((preset) => (
                      <Button
                        key={preset.id}
                        variant="light"
                        color={preset.color || 'blue'}
                        leftSection={preset.icon}
                        justify="start"
                        onClick={() => handleSelectPreset(preset)}
                        fullWidth
                        styles={{
                          inner: { justifyContent: 'flex-start' },
                          label: { flex: 1 },
                        }}
                      >
                        <Group justify="apart" style={{ width: '100%' }}>
                          <div>
                            <div style={{ fontWeight: 600 }}>{preset.name}</div>
                            <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                              {preset.description}
                            </div>
                          </div>
                          {preset.badge && (
                            <Badge size="xs" variant="dot">
                              {preset.badge}
                            </Badge>
                          )}
                        </Group>
                      </Button>
                    ))}
                    {filteredPresets.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '1rem', opacity: 0.5 }}>
                        No presets found
                      </div>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}

            {/* Saved Filters - Favorites */}
            {savedFilters.length > 0 && favoriteFilters.length > 0 && (
              <Accordion.Item value="favorites">
                <Accordion.Control>
                  <Group justify="apart">
                    <Group gap="xs">
                      <IconStarFilled size={16} style={{ color: 'var(--mantine-color-yellow-6)' }} />
                      <span>Favorite Filters</span>
                    </Group>
                    <Badge size="sm" variant="light" color="yellow">
                      {favoriteFilters.length}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    {favoriteFilters.map((saved) => (
                      <Group key={saved.id} gap="xs" wrap="nowrap">
                        <Button
                          variant="light"
                          color="blue"
                          justify="start"
                          onClick={() => handleLoadSaved(saved)}
                          style={{ flex: 1 }}
                          styles={{
                            inner: { justifyContent: 'flex-start' },
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600 }}>{saved.name}</div>
                            {saved.description && (
                              <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                                {saved.description}
                              </div>
                            )}
                          </div>
                        </Button>
                        <Tooltip label="Remove from favorites">
                          <ActionIcon
                            variant="subtle"
                            color="yellow"
                            onClick={() => onToggleFavorite?.(saved.id)}
                          >
                            <IconStarFilled size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete filter">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => onDeleteSaved?.(saved.id)}
                          >
                            <IconX size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    ))}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}

            {/* Saved Filters - Regular */}
            {savedFilters.length > 0 && regularFilters.length > 0 && (
              <Accordion.Item value="saved">
                <Accordion.Control>
                  <Group justify="apart">
                    <span>Saved Filters</span>
                    <Badge size="sm" variant="light">
                      {regularFilters.length}
                    </Badge>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack gap="xs">
                    {regularFilters.map((saved) => (
                      <Group key={saved.id} gap="xs" wrap="nowrap">
                        <Button
                          variant="light"
                          color="gray"
                          justify="start"
                          onClick={() => handleLoadSaved(saved)}
                          style={{ flex: 1 }}
                          styles={{
                            inner: { justifyContent: 'flex-start' },
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600 }}>{saved.name}</div>
                            {saved.description && (
                              <div style={{ fontSize: '0.75rem', opacity: 0.7 }}>
                                {saved.description}
                              </div>
                            )}
                          </div>
                        </Button>
                        <Tooltip label="Add to favorites">
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={() => onToggleFavorite?.(saved.id)}
                          >
                            <IconStar size={18} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete filter">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => onDeleteSaved?.(saved.id)}
                          >
                            <IconX size={18} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    ))}
                    {regularFilters.length === 0 && (
                      <div style={{ textAlign: 'center', padding: '1rem', opacity: 0.5 }}>
                        No saved filters found
                      </div>
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            )}

            {/* Custom Filters */}
            <Accordion.Item value="filters">
              <Accordion.Control>
                <Group justify="apart">
                  <span>Custom Filters</span>
                  {activeFilterCount > 0 && (
                    <Badge size="sm" variant="filled" color="blue">
                      {activeFilterCount} active
                    </Badge>
                  )}
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">{children}</Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </ScrollArea>

        {/* Action buttons */}
        <Group justify="apart" mt="md">
          <Group>
            <Button variant="subtle" onClick={handleClear} disabled={activeFilterCount === 0}>
              Clear All
            </Button>
            {onSave && activeFilterCount > 0 && (
              <Button
                variant="light"
                leftSection={<IconStar size={16} />}
                onClick={() => {
                  const name = prompt('Enter a name for this filter:');
                  if (name) {
                    const description = prompt('Enter a description (optional):');
                    onSave(name, description || undefined);
                  }
                }}
              >
                Save as Favorite
              </Button>
            )}
          </Group>
          <Group>
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleApply}>Apply Filters</Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
